import { ScrollView, StyleSheet, TextInput } from 'react-native';
import React, { useContext } from 'react';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { SOTFlowType } from '../types';
import { Text, View } from '../components/Themed';
import ButtonStandard from '../components/buttons/ButtonStandard';
import NavHeader from '../components/LayoutSections/NavHeader';
import { SoTContext } from '../Global/SoTContext';
import { globalStyles } from '../Global/GlobalStyles';
import ExpandingTextInput from '../components/ExpandingTextInput';

type Props = NativeStackScreenProps<SOTFlowType>;
export default function ShareInsightScreen({ navigation }: Props) {
  console.log('ShareInsightScreen');

  const {
    user,
    oneSentenceInsights,
    setOnceSentenceInsights,
    completeTheSentenceInsights,
    setCompleteTheSentenceInsights,
    personalStoryInsights,
    setPersonalStoryInsights,
    api,
    userExtraData,
    setUserExtraData,
    askAboutInsights,
    setAskAboutInsights,
    mobile,
  } = useContext(SoTContext);

  const readyMoveNext = (fields: Array<string>) => {
    return fields?.filter(f => f === '')?.length === 0;
  };

  const onNext = async () => {
    try {
      const req = {
        ...userExtraData,
        oneSentence: oneSentenceInsights,
        completeTheSentence: completeTheSentenceInsights,
        story: personalStoryInsights,
        lastPage: userExtraData.completed ? 'Review' : 'ShareMedia',
        askAbout: askAboutInsights,
      };
      setUserExtraData(req);
      console.log('save ', req);
      await api.setStoredDetails(userExtraData?.id, req);
      navigation.navigate('ShareMedia');
    } catch (error) {
      console.log('error : ', error);
    }
  };

  const placeholderText = `This is your space to share a story or moment about ${
    user?.first
  }. It could be about ${
    user?.gender === 'Male' ? 'his' : 'her'
  } dominant trait, something most people wouldn't know about ${
    user?.gender === 'Male' ? 'him' : 'her'
  }, or a funny or meaningful memory that shows who ${user?.gender === 'Male' ? 'he' : 'she'} is.`;
  return (
    <View style={styles.container}>
      <NavHeader
        backButtonNavigation="DominantTrait"
        props={navigation}
        steps={[1, 1, 1, 1, 1, 1, 0]}
        width={globalStyles.container.width}
        height="112px"
      ></NavHeader>
      <ScrollView style={{ paddingHorizontal: 20, width: '100%' }}>
        <View style={{ width: '100%' }}>
          <Text style={styles.title}>Describe {user?.first} in your own words</Text>
          <View style={{ flexDirection: 'row' }}>
            <Text style={styles.subTitle}>
              Think of the moments that reveal who {user?.first} is.{'\n'}
              That’s what we’re here to capture.
            </Text>
          </View>
        </View>
        <View style={styles.separator} lightColor="#eee" darkColor="rgba(255,255,255,0.1)" />
        <View style={styles.userInfoEntry}>
          <Text style={styles.fieldTopLabel}>Describe {user?.first} in one sentence</Text>
          <ExpandingTextInput
            placeholderTextColor="#ADB5BD"
            value={oneSentenceInsights}
            placeholder={`How would you describe ${user.first} if you were introducing ${
              user.gender === 'Female' ? 'her' : 'him'
            } to someone amazing?`}
            style={styles.userInputField}
            minHeight={80}
            onChangeText={text => setOnceSentenceInsights(text)}
          ></ExpandingTextInput>
        </View>
        <View style={styles.userInfoEntry}>
          <Text style={styles.fieldTopLabel}>I can count on {user?.first} to</Text>
          <ExpandingTextInput
            placeholderTextColor="#ADB5BD"
            value={completeTheSentenceInsights}
            placeholder={`What's one of ${user?.first}'s quirks?`}
            style={styles.userInputField}
            onChangeText={text => setCompleteTheSentenceInsights(text)}
          ></ExpandingTextInput>
        </View>
        <View style={styles.userInfoEntry}>
          <Text style={styles.fieldTopLabel}>Ask {user.first} about</Text>
          <ExpandingTextInput
            placeholderTextColor="#ADB5BD"
            value={askAboutInsights}
            placeholder={`Break the ice for ${user.first}`}
            style={styles.userInputField}
            onChangeText={text => setAskAboutInsights(text)}
          ></ExpandingTextInput>
        </View>
        <View style={styles.userInfoEntry}>
          <Text style={styles.fieldTopLabel}>A story only I can tell</Text>
          <TextInput
            style={styles.userPersonalStory}
            value={personalStoryInsights}
            placeholderTextColor="#ADB5BD"
            placeholder={`What’s a story that made you say “Yep, that’s totally ${user.first}”?`}
            multiline={true}
            numberOfLines={5}
            onChangeText={text => setPersonalStoryInsights(text)}
          ></TextInput>
        </View>

        {globalStyles.container.height < 800 && (
          <View
            style={{
              width: '100%',
              paddingTop: 40,
              paddingBottom: 10,
            }}
          >
            <ButtonStandard
              disabled={
                user.phone !== mobile.replace(/[^\w]/g, '') &&
                !readyMoveNext([
                  oneSentenceInsights,
                  completeTheSentenceInsights,
                  askAboutInsights,
                  personalStoryInsights,
                ])
              }
              label="Next"
              onPress={() => {
                if (user.phone === mobile.replace(/[^\w]/g, '')) {
                  navigation.navigate('ShareMedia');
                } else {
                  onNext();
                }
              }}
            ></ButtonStandard>
          </View>
        )}
      </ScrollView>
      {globalStyles.container.height >= 800 && (
        <View
          style={{
            width: '100%',
            paddingVertical: 20,
            paddingHorizontal: 20,
          }}
        >
          <ButtonStandard
            disabled={
              user.phone !== mobile.replace(/[^\w]/g, '') &&
              !readyMoveNext([
                oneSentenceInsights,
                completeTheSentenceInsights,
                askAboutInsights,
                personalStoryInsights,
              ])
            }
            label="Next"
            onPress={() => {
              if (user.phone === mobile.replace(/[^\w]/g, '')) {
                navigation.navigate('ShareMedia');
              } else {
                onNext();
              }
            }}
          ></ButtonStandard>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    backgroundColor: '#FFFFFF',
    width: `${globalStyles.container.width}px`,
    margin: 'auto',
  },
  title: {
    fontFamily: 'Ubuntu',
    fontWeight: '500',
    fontSize: globalStyles.container.height < 800 ? 22 : 26,
    color: '#212529',
    marginBottom: 8,
  },
  subTitle: {
    fontFamily: 'Jost Regular',
    fontSize: globalStyles.container.height < 800 ? 16 : 18,
    color: '#6C7570',
  },
  separator: {
    marginVertical: 10,
    marginBottom: 0,
    margin: 'auto',
    height: 1,
    backgroundColor: '#DEE2E6',
    width: '100%',
  },
  userInfoEntry: {
    width: '100%',
    marginTop: 20,
  },
  userPersonalStory: {
    width: '100%',
    height: 208,
    paddingTop: 9,
    paddingLeft: 10,
    paddingRight: 10,
    marginTop: 0,

    fontFamily: 'Jost Regular',
    fontSize: 18,
    color: '#212529',

    borderColor: '#ADB5BD',
    borderRadius: 8,
    borderWidth: 2,
    // paddingLeft: 10,
  },
  fieldTopLabel: {
    color: '#212529',
    fontFamily: 'Jost Regular',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 6,
  },
  userInputField: {
    fontFamily: 'Jost Regular',
    fontSize: 18,
    color: '#212529',
    width: 'calc(100% - 25px)',
    height: 50,
    borderColor: '#ADB5BD',
    borderRadius: 8,
    borderWidth: 2,
    paddingLeft: 10,
  },
});
